import * as React from 'react';
import BPromise from 'bluebird';
import { History, Location } from 'history';
import { parse } from 'query-string';
import { Grid2 } from 'Src/components/Grid/Grid2';
import { Loader } from 'Src/components/Loader/Loader';
import { ActionItem } from 'Src/components/ActionsButton/ActionsButton';
import { DATE_INTERVAL, FILTER_TYPE, FIRST_PAGE } from 'Src/consts/table';
import {
  ColumnDefinition,
  getFilters2,
  getOrder,
  getSearchFilter,
  getSearchOrder,
  getServerFieldSectionWhere,
  getServerQueryFilter2,
  isFilterExist2,
  isSortExist,
  TABLE_SORT_DIRECTION
} from 'Src/helpers/table/table';
import {
  deleteSuperAdminSport,
  getAllSuperAdminSports,
  getSuperAdminSports,
  getSuperAdminSportsCount,
  SuperAdminSport
} from 'Src/helpers/service/superadmin/sports';

const SCORING_LABELS = {
  MORE_SCORES: 'More scores',
  LESS_SCORES: 'Less scores',
  MORE_TIME: 'More time',
  LESS_TIME: 'Less time',
  MORE_RESULT: 'More result',
  LESS_RESULT: 'Less result',
  FIRST_TO_N_POINTS: 'First to n points'
};

const PLAYERS_LABELS = {
  '1X1': '1x1',
  '2X2': '2x2',
  INDIVIDUAL: 'Individual',
  TEAM: 'Team'
};

function formatBoolean(value: any, trueText: string, falseText: string): string {
  return value === true ? trueText : falseText;
}

function getLabel(map: any, value?: string): string {
  return value && map[value] ? map[value] : value || '';
}

const COLUMNS: ColumnDefinition[] = [
  {
    text: 'Name',
    field: 'name',
    isSort: true,
    type: FILTER_TYPE.TEXT,
    accessor: ['name']
  },
  {
    text: 'Description',
    field: 'description',
    isSort: true,
    type: FILTER_TYPE.TEXT,
    accessor: ['description']
  },
  {
    text: 'Players',
    field: 'players',
    isSort: true,
    type: FILTER_TYPE.TEXT,
    accessor: (item: SuperAdminSport) => getLabel(PLAYERS_LABELS, item.players)
  },
  {
    text: 'Scoring',
    field: 'scoring',
    isSort: true,
    type: FILTER_TYPE.TEXT,
    accessor: (item: SuperAdminSport) => getLabel(SCORING_LABELS, item.scoring)
  },
  {
    text: 'Multiparty',
    field: 'multiparty',
    isSort: true,
    type: FILTER_TYPE.NONE,
    accessor: (item: SuperAdminSport) => formatBoolean(item.multiparty, 'Multiparty', 'Not multiparty')
  },
  {
    text: 'School events',
    field: 'isSchoolEventsAvailable',
    isSort: true,
    type: FILTER_TYPE.NONE,
    accessor: (item: SuperAdminSport) => formatBoolean(item.isSchoolEventsAvailable, 'Yes', 'No')
  },
  {
    text: 'Club events',
    field: 'isClubEventsAvailable',
    isSort: true,
    type: FILTER_TYPE.NONE,
    accessor: (item: SuperAdminSport) => formatBoolean(item.isClubEventsAvailable, 'Yes', 'No')
  }
];

interface Props {
  history: History;
  location: Location;
}

interface State {
  items: SuperAdminSport[];
  currentPage: number;
  selectedItems: SuperAdminSport[];
  sortDirection: TABLE_SORT_DIRECTION;
  sortColumnsName: string;
  isShowFilter: boolean;
  filters: any;
  isDataFiltered: boolean;
  isLoading: boolean;
  total: number;
  basePath: string;
  isSelectAllChecked: boolean;
  errorMessage: string;
  successMessage: string;
}

export class SuperAdminSports extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      items: [],
      currentPage: FIRST_PAGE,
      selectedItems: [],
      sortDirection: '',
      sortColumnsName: '',
      isShowFilter: false,
      filters: {},
      isDataFiltered: false,
      isLoading: false,
      total: 0,
      basePath: '',
      isSelectAllChecked: false,
      errorMessage: '',
      successMessage: ''
    };
  }

  componentDidMount() {
    this.setState({ isLoading: true, errorMessage: '', successMessage: '' });
    this.setItems();
  }

  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    if (nextProps.location.search !== this.props.location.search) {
      this.setState({ isLoading: true, errorMessage: '', successMessage: '' });
      this.setItems();
    }
  }

  setItems() {
    const { history } = this.props;
    const search = parse(history.location.search);
    const page = typeof search.page !== 'undefined' ? Number(search.page) : FIRST_PAGE;

    let sortDirection: TABLE_SORT_DIRECTION = '';
    let sortColumnsName = '';

    if (typeof search.order === 'string') {
      [sortColumnsName, sortDirection] = search.order.split(':') as [string, TABLE_SORT_DIRECTION];
    }

    const sortByFieldExist = sortColumnsName !== '';
    const sortDirectionExist = sortDirection !== '';
    const filters = getFilters2(COLUMNS, search);
    const isShowFilter = isFilterExist2(filters);
    const isDataFiltered = isFilterExist2(filters);
    const where = getServerFieldSectionWhere(filters);
    const order = sortByFieldExist && sortDirectionExist ? `${sortColumnsName} ${sortDirection}` : 'name ASC';
    const serverQueryFilter = getServerQueryFilter2(page, where, order);

    return BPromise.all([getSuperAdminSportsCount(where), getSuperAdminSports(serverQueryFilter)])
      .then(([countObj, items]) => {
        this.setState({
          currentPage: page,
          sortDirection,
          sortColumnsName,
          isShowFilter,
          isDataFiltered,
          filters,
          items,
          total: countObj.count,
          basePath: history.location.pathname,
          isLoading: false,
          errorMessage: ''
        });

        return true;
      })
      .catch(error => {
        console.error('Can not load superadmin sports', error);
        this.setState({
          isLoading: false,
          errorMessage: 'Can not load sports. Please check the session and try again.'
        });
      });
  }

  setCurrentPageParams = (currentPage: number): void => {
    const search: string[] = [];
    const { filters, sortColumnsName, sortDirection } = this.state;

    if (currentPage !== FIRST_PAGE) {
      search.push(`page=${currentPage}`);
    }

    if (isSortExist(sortDirection, sortColumnsName)) {
      search.push(getSearchOrder(sortDirection, sortColumnsName));
    }

    if (isFilterExist2(filters)) {
      search.push(getSearchFilter(filters));
    }

    this.props.history.push({
      pathname: this.state.basePath,
      search: search.join('&')
    });
  };

  onItemClick = (index: number): void => {
    const { items, selectedItems } = this.state;
    const selectedItem = items[index];
    const selectedItemIndex = selectedItems.findIndex(item => selectedItem.id === item.id);
    const selectedItemsUpdated = [...selectedItems];

    if (selectedItemIndex !== -1) {
      selectedItemsUpdated.splice(selectedItemIndex, 1);
    } else {
      selectedItemsUpdated.push(selectedItem);
    }

    this.setState({ selectedItems: selectedItemsUpdated });
  };

  onSelectAllOnPageClick = (): void => {
    const { items, selectedItems } = this.state;
    const itemsFiltered = items.filter(item => selectedItems.every(selectedItem => selectedItem.id !== item.id));

    this.setState({
      selectedItems: [...selectedItems, ...itemsFiltered],
      isSelectAllChecked: true
    });
  };

  onSelectAllClick = (): void => {
    const { selectedItems, filters } = this.state;
    const where = getServerFieldSectionWhere(filters);

    this.setState({ isLoading: true, errorMessage: '', successMessage: '' });

    getAllSuperAdminSports(where)
      .then(items => {
        const itemsFiltered = items.filter(item => selectedItems.every(selectedItem => selectedItem.id !== item.id));

        this.setState({
          selectedItems: [...selectedItems, ...itemsFiltered],
          isSelectAllChecked: true,
          isLoading: false
        });
      })
      .catch(error => {
        console.error('Can not select all superadmin sports', error);
        this.setState({
          isLoading: false,
          errorMessage: 'Can not select all sports. Please try again.'
        });
      });
  };

  onUnselectAllClick = (): void => {
    this.setState({
      selectedItems: [],
      isSelectAllChecked: false
    });
  };

  onTableSortClick = (sortField: string): void => {
    const { sortDirection, sortColumnsName, filters } = this.state;
    const order = getOrder(sortField, sortDirection, sortColumnsName);
    const search: string[] = [`order=${sortField}:${order}`];

    if (isFilterExist2(filters)) {
      search.push(getSearchFilter(filters));
    }

    this.props.history.push({
      pathname: this.state.basePath,
      search: search.join('&')
    });
  };

  onTableFilterChange = (event, filterField: string, options?): void => {
    const filterValue = event.target.value;
    const filters = this.state.filters;
    const currentFilterField = filters[filterField];
    let nextFilters = { ...filters };

    if (typeof options !== 'undefined') {
      switch (options) {
        case DATE_INTERVAL.FROM:
          nextFilters = { ...nextFilters, [filterField]: { ...currentFilterField, from: filterValue } };
          break;
        case DATE_INTERVAL.TO:
          nextFilters = { ...nextFilters, [filterField]: { ...currentFilterField, to: filterValue } };
          break;
      }
    } else {
      nextFilters = { ...nextFilters, [filterField]: filterValue };
    }

    this.setState({ filters: nextFilters });
  };

  onApplyFilterClick = (): void => {
    const { filters, sortDirection, sortColumnsName } = this.state;
    const search: string[] = [];

    if (isSortExist(sortDirection, sortColumnsName)) {
      search.push(getSearchOrder(sortDirection, sortColumnsName));
    }

    if (isFilterExist2(filters)) {
      search.push(getSearchFilter(filters));
    }

    this.setState({ selectedItems: [], isSelectAllChecked: false });

    this.props.history.push({
      pathname: this.state.basePath,
      search: search.join('&')
    });
  };

  onClearFilterClick = (): void => {
    this.setState({ selectedItems: [], isSelectAllChecked: false });

    this.props.history.push({
      pathname: this.state.basePath,
      search: ''
    });
  };

  onTableFilterClick = (event): void => {
    event.preventDefault();
    this.setState({ isShowFilter: !this.state.isShowFilter });
  };

  onAddSportClick = (): void => {
    this.props.history.push('/sports/add');
  };

  onEditSportClick = (): void => {
    const { selectedItems } = this.state;

    if (selectedItems.length !== 1) {
      return;
    }

    this.props.history.push(`/sports/${selectedItems[0].id}/edit`);
  };

  onDeleteSportClick = (): void => {
    const { selectedItems } = this.state;

    if (selectedItems.length !== 1) {
      return;
    }

    const selectedSport = selectedItems[0];
    const isConfirmed = window.confirm(`Do you really want to remove ${selectedSport.name || 'this sport'}?`);

    if (!isConfirmed) {
      return;
    }

    this.setState({ isLoading: true, errorMessage: '', successMessage: '' });

    deleteSuperAdminSport(selectedSport.id)
      .then(() => {
        this.setState({ selectedItems: [], isSelectAllChecked: false, successMessage: 'Sport has been removed.' });
        return this.setItems();
      })
      .catch(error => {
        console.error('Can not delete superadmin sport', error);
        this.setState({
          isLoading: false,
          errorMessage: 'Can not delete sport. It may be used by events, tournaments, or schools.'
        });
      });
  };

  render() {
    const {
      currentPage,
      errorMessage,
      filters,
      isDataFiltered,
      isLoading,
      isSelectAllChecked,
      isShowFilter,
      items,
      selectedItems,
      sortColumnsName,
      sortDirection,
      successMessage,
      total
    } = this.state;

    const actionItems: ActionItem[] = [
      {
        itemText: 'Edit sport',
        isActive: selectedItems.length === 1,
        onItemClick: this.onEditSportClick
      },
      {
        itemText: 'Delete sport',
        isActive: selectedItems.length === 1,
        onItemClick: this.onDeleteSportClick
      }
    ];

    if (isLoading) {
      return <Loader />;
    }

    return (
      <div className="container-fluid py-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h2 className="mb-1">Sports</h2>
            <div className="text-muted">Superadmin sports list using the shared frontend2 grid.</div>
          </div>
          <div>
            <button className="btn btn-primary mr-2" onClick={this.onAddSportClick}>
              Add sport
            </button>
            <button className="btn btn-outline-secondary" onClick={() => this.setItems()}>
              Refresh
            </button>
          </div>
        </div>

        {errorMessage && <div className="alert alert-danger">{errorMessage}</div>}
        {successMessage && <div className="alert alert-success">{successMessage}</div>}

        <Grid2
          dataItems={items}
          filters={filters}
          currentPage={currentPage}
          total={total}
          isSelectAllChecked={isSelectAllChecked}
          selectedItemsCount={selectedItems.length}
          isDataFiltered={isDataFiltered}
          sortDirection={sortDirection}
          sortColumnsName={sortColumnsName}
          isShowFilter={isShowFilter}
          dataItemsSelected={selectedItems}
          columns={COLUMNS}
          actionItems={actionItems}
          onItemClick={this.onItemClick}
          onSortClick={this.onTableSortClick}
          onApplyFilterClick={this.onApplyFilterClick}
          onClearFilterClick={this.onClearFilterClick}
          onTableFilterChange={this.onTableFilterChange}
          onTableFilterClick={this.onTableFilterClick}
          setCurrentPageParams={this.setCurrentPageParams}
          onSelectAllClick={this.onSelectAllClick}
          onSelectAllOnPageClick={this.onSelectAllOnPageClick}
          onUnselectAllClick={this.onUnselectAllClick}
          isCSV={true}
        />
      </div>
    );
  }
}

